import { useState } from 'react';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

interface SecretDisplayDialogProps {
    open: boolean;
    onClose: () => void;
    clientId: string;
    clientSecret: string;
    name: string;
}

export function SecretDisplayDialog({
    open,
    onClose,
    clientId,
    clientSecret,
    name,
}: SecretDisplayDialogProps) {
    const [copiedId, setCopiedId] = useState(false);
    const [copiedSecret, setCopiedSecret] = useState(false);

    const copyToClipboard = async (text: string, type: 'id' | 'secret') => {
        try {
            await navigator.clipboard.writeText(text);
            if (type === 'id') {
                setCopiedId(true);
                setTimeout(() => setCopiedId(false), 2000);
            } else {
                setCopiedSecret(true);
                setTimeout(() => setCopiedSecret(false), 2000);
            }
        } catch {
            // ignore
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>OAuth Client Created</DialogTitle>
                    <DialogDescription>
                        Credentials for <span className="font-medium">{name}</span>
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                        <p className="text-sm text-amber-800">
                            Store the client secret securely. It will not be shown again.
                        </p>
                    </div>

                    <div className="space-y-2">
                        <Label>Client ID</Label>
                        <div className="flex gap-2">
                            <Input readOnly value={clientId} className="font-mono text-sm" />
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => copyToClipboard(clientId, 'id')}
                            >
                                {copiedId ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            </Button>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Client Secret</Label>
                        <div className="flex gap-2">
                            <Input readOnly value={clientSecret} className="font-mono text-sm" />
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => copyToClipboard(clientSecret, 'secret')}
                            >
                                {copiedSecret ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            </Button>
                        </div>
                    </div>

                    <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
                        <p className="font-medium text-slate-900">Usage</p>
                        <p>
                            Exchange credentials at the token endpoint with{' '}
                            <code className="rounded bg-white px-1">grant_type=client_credentials</code>{' '}
                            using HTTP Basic authentication (client_id:client_secret).
                        </p>
                        <p className="mt-1">
                            Use the returned JWT Bearer token to invoke capabilities via the HTTP gateway.
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button onClick={onClose}>Done</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
